import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { companyI18n } from './i18n';

export default function BranchCreateEditModal({
  Modal,
  Input,
  show,
  onClose,
  mode = 'create',
  initialData = null,
  onSave,
  onRequestDelete,
  titleCreate,
  titleEdit,
  saveLabel,
  cancelLabel,
  deleteLabel,
  width = '720px',
  Dropdown,
}) {
  const { language } = useLanguage();
  const companyLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: companyI18n });
  const isEdit = mode === 'edit';

  const resolvedTitleCreate = titleCreate || t('company_modal_createTitle');
  const resolvedTitleEdit = titleEdit || t('company_modal_editTitle');
  const resolvedSaveLabel = saveLabel || t('company_modal_save');
  const resolvedCancelLabel = cancelLabel || t('company_modal_cancel');

  const statusOptions = useMemo(
    () => [
      {
        label: t('company_modal_statusActive'),
        value: t('company_modal_statusActive'),
      },
      {
        label: t('company_modal_statusInactive'),
        value: t('company_modal_statusInactive'),
      },
    ],
    [t]
  );

  const statusOptionFrom = (val) =>
    statusOptions.find((o) => o.value === val) ?? null;

  const [form, setForm] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    status: '',
  });

  useEffect(() => {
    companyI18n.changeLanguage(companyLanguage);
  }, [companyLanguage]);

  useEffect(() => {
    if (!show) return;

    const activeStatus = t('company_modal_statusActive');

    if (isEdit && initialData) {
      setForm({
        name: initialData.name || '',
        address: initialData.address || '',
        phone: initialData.phone || '',
        email: initialData.email || '',
        status: initialData.status || activeStatus,
      });
    } else {
      setForm({
        name: '',
        address: '',
        phone: '',
        email: '',
        status: activeStatus,
      });
    }
  }, [show, isEdit, initialData, t]);

  const onChange = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const emailValid = useMemo(() => {
    if (!form.email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  }, [form.email]);

  const phoneValid = useMemo(() => {
    if (!form.phone) return false;
    return form.phone.replace(/\D/g, '').length >= 7;
  }, [form.phone]);

  const isValid = useMemo(() => {
    return (
      form.name.trim() &&
      form.address.trim() &&
      phoneValid &&
      emailValid &&
      (form.status === t('company_modal_statusActive') ||
        form.status === t('company_modal_statusInactive'))
    );
  }, [form, phoneValid, emailValid, t]);

  const handleSave = () => {
    if (!isValid) return;

    const payload = {
      ...(isEdit && initialData?.id ? { id: initialData.id } : {}),
      name: form.name.trim(),
      address: form.address.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      status: form.status,
      isMain: initialData?.isMain ?? false,
    };

    onSave?.(payload);
  };

  return (
    <Modal
      title={isEdit ? resolvedTitleEdit : resolvedTitleCreate}
      show={show}
      onClose={onClose}
      primaryBtnTxt={resolvedSaveLabel}
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid}
      secondaryBtnTxt={resolvedCancelLabel}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width={width}
    >
      <div className='row g-3'>
        <div className='col-12'>
          <Input
            label={t('company_modal_nameLabel')}
            required
            placeholder={t('company_modal_namePlaceholder')}
            value={form.name}
            onChange={onChange('name')}
          />
        </div>

        <div className='col-12'>
          <Input
            label={t('company_modal_addressLabel')}
            required
            placeholder={t('company_modal_addressPlaceholder')}
            value={form.address}
            onChange={onChange('address')}
          />
        </div>

        <div className='col-12 col-md-6'>
          <Input
            label={t('company_modal_phoneLabel')}
            required
            placeholder={t('company_modal_phonePlaceholder')}
            value={form.phone}
            onChange={onChange('phone')}
            error={
              form.phone && !phoneValid ? t('company_modal_phoneInvalid') : ''
            }
            inputMode='tel'
          />
        </div>

        <div className='col-12 col-md-6'>
          <Input
            label={t('company_modal_emailLabel')}
            required
            placeholder={t('company_modal_emailPlaceholder')}
            value={form.email}
            onChange={onChange('email')}
            error={
              form.email && !emailValid ? t('company_modal_emailInvalid') : ''
            }
            inputMode='email'
          />
        </div>

        <div className='col-12'>
          <div className='d-flex'>
            <label className='form-label'>{t('company_modal_statusLabel')}</label>
          </div>
          <Dropdown
            options={statusOptions}
            value={statusOptionFrom(form.status)}
            onChange={(opt) =>
              setForm((prev) => ({
                ...prev,
                status: opt?.value || t('company_modal_statusActive'),
              }))
            }
            placeholder={t('company_modal_statusPlaceholder')}
            width='100%'
          />
        </div>
      </div>
    </Modal>
  );
}
